import { createAsyncThunk } from "@reduxjs/toolkit";
import AiApiService from "../apiServices/AiApiService";
import AnalyzeImageFileStore from "../helpers/AnalyzeImageFileStore";
import {
  BulkImageAnalyzeContext,
  BulkImageAnalyzeResponse,
  ANALYZE_IMAGES_PER_CALL,
} from "../../interfaces/ImageAnalyzeObject";
// From the keys module, NOT from ImageAnalyzeDb: the slice imports these thunks
// for its extraReducers, so importing the slice back from here would put the
// two files in a runtime cycle.
import {
  analyzeBatchKey,
  analyzeImageKey,
} from "../../dataManagement/uiDb/ImageAnalyzeKeys";
// Type-only, so this import is erased at compile time and adds no runtime edge.
import type { RootState } from "../../dataManagement/uiDb/UiDb";

/**
 * Async thunks for the curator's photo-analysis flow.
 *
 * Two levels, and the split is the whole design:
 *
 *   analyzeImages  one request carrying 1..ANALYZE_IMAGES_PER_CALL photos. The
 *                  only thing that ever writes image or product state (see
 *                  ImageAnalyzeDb) — which is what makes a manual single-photo
 *                  retry and a full group indistinguishable to the reducer:
 *                  a retry is simply a group of one.
 *   analyzeBatch   sequencing only. Splits a batch into chunks and awaits one
 *                  chunk at a time.
 *
 * Nothing here is aborted on unmount, deliberately. A batch is slow and
 * expensive, and a curator who closes the modal to check something on the
 * business page should come back to finished work, not a cancelled run. The
 * thunk lives in the store, so it keeps going regardless of what is mounted.
 *
 * Rejections carry an `errorCode` alongside the message so each tile can render
 * curator-facing copy from ANALYZE_ERROR_COPY rather than whatever the server
 * said. `ANALYZE_FILE_MISSING` is the one code we mint ourselves.
 */

export interface AnalyzeRejection {
  errorCode: string | null;
  message: string;
}

interface AnalyzeImagesArg {
  setupId: string;
  /** Sent together, in this order. The response's `img_N` refs correspond to
   *  this array by position — see applyAnalyzeResponse. */
  imageIds: string[];
  context: BulkImageAnalyzeContext;
}

interface AnalyzeImagesResult {
  imageIds: string[];
  response: BulkImageAnalyzeResponse;
}

/**
 * Analyses one group of photos in a single request.
 *
 * Sending several at once is the point: the endpoint decides for itself how
 * many products the set depicts, so a group call is what lets two shots of the
 * same car become one draft instead of two. It also cuts round trips, which at
 * 5–20s per vision call is most of the wall-clock a curator waits through.
 *
 * The cost is that the group, not the photo, becomes the unit of failure for
 * the request itself — though per-image outcomes still come back individually
 * inside the response, so one unreadable photo does not sink its group-mates.
 *
 * `imageIds` order is load-bearing: files are appended in this order, the
 * backend assigns `img_0..img_N` in upload order, and the reducer maps refs
 * back to these ids by that correspondence.
 */
export const analyzeImages = createAsyncThunk<
  AnalyzeImagesResult,
  AnalyzeImagesArg,
  { state: RootState; rejectValue: AnalyzeRejection }
>(
  "imageAnalyze/analyzeImages",
  async ({ imageIds, context }, { rejectWithValue }) => {
    const files = imageIds.map((id) => AnalyzeImageFileStore.get(id));

    // The bytes are gone — almost always a page refresh, since analysis state
    // is rehydrated but Files cannot be. Rejected with our own code so the tile
    // says "pick it again" rather than offering a retry that can only fail.
    // All-or-nothing for the group: sending a partial set would silently drop a
    // photo the curator can still see queued on screen.
    if (files.some((file) => !file)) {
      return rejectWithValue({
        errorCode: "ANALYZE_FILE_MISSING",
        message: "Those photos are no longer available. Remove them and pick them again.",
      });
    }

    try {
      const response = await AiApiService.bulkImageAnalyze(files as File[], context);

      // A total failure reported inside a 200 body. The documented contract says
      // `error` is only non-null when the status is not 200 (and throwIfNotOk
      // would have caught that), but trusting a response not to contradict its
      // own docs is how a tile ends up green with nothing behind it.
      if (response?.error) {
        return rejectWithValue({ errorCode: null, message: response.error });
      }

      return { imageIds, response };
    } catch (error) {
      return rejectWithValue({
        errorCode: null,
        message:
          error instanceof Error
            ? error.message
            : "Couldn't analyse those photos. Please try again.",
      });
    }
  },
  {
    /** Blocks a second run over any photo already in flight — a double-tapped
     *  retry, or a batch reaching an image whose own retry is still running. */
    condition: ({ setupId, imageIds }, { getState }) => {
      const { loadingMap } = getState().imageAnalyze;
      return !imageIds.some((id) => loadingMap[analyzeImageKey(setupId, id)]);
    },
  }
);

interface AnalyzeBatchArg {
  setupId: string;
  batchId: string;
  context: BulkImageAnalyzeContext;
}

/** Consecutive chunks of at most `size`. Order is preserved, so the curator
 *  watches tiles resolve left to right regardless of how they were grouped. */
const chunk = <T,>(items: T[], size: number): T[][] => {
  const groups: T[][] = [];
  const step = Math.max(1, size);
  for (let i = 0; i < items.length; i += step) {
    groups.push(items.slice(i, i + step));
  }
  return groups;
};

/**
 * Runs one batch, a chunk at a time, starting the next as soon as the previous
 * settles.
 *
 * Chunks run sequentially rather than all at once because several concurrent
 * vision calls is how you find the rate limit, and because a curator watching
 * tiles fill in can tell where a run got to. Batches themselves ARE allowed to
 * run concurrently — each carries its own loading key — so starting batch 2
 * while batch 1 is still going does exactly that.
 *
 * A failed chunk does not stop the run. `dispatch` of a thunk resolves with the
 * settled action rather than throwing (only `.unwrap()` throws, which is
 * pointedly not called here), so continue-on-failure is the natural behaviour
 * and not a special case: the failed photos go red, keep their own retry, and
 * the loop moves on.
 *
 * State is re-read from the store before chunking, never captured at click
 * time — over a run lasting a minute or more the curator may have removed a
 * photo or retried one by hand.
 */
export const analyzeBatch = createAsyncThunk<
  void,
  AnalyzeBatchArg,
  { state: RootState }
>(
  "imageAnalyze/analyzeBatch",
  async ({ setupId, batchId, context }, { dispatch, getState }) => {
    const session = getState().imageAnalyze.sessions[setupId];
    const batch = session?.batches.find((b) => b.id === batchId);
    if (!session || !batch) return;

    // `done` and `unassigned` already had their call — re-running them would
    // spend a model call and a storage write to reach the same answer.
    // `orphaned` has no bytes left to send. `analyzing` is someone else's
    // in-flight retry. Only work that has not succeeded yet is picked up, which
    // is what also makes this button a safe "run the rest".
    const runnable = batch.imageIds.filter((id) => {
      const status = session.images[id]?.status;
      return status === "queued" || status === "failed";
    });

    for (const group of chunk(runnable, ANALYZE_IMAGES_PER_CALL)) {
      // Re-checked per chunk: a photo may have been removed while an earlier
      // chunk was in flight.
      const live = group.filter((id) => {
        const status =
          getState().imageAnalyze.sessions[setupId]?.images[id]?.status;
        return status === "queued" || status === "failed";
      });
      if (live.length === 0) continue;

      await dispatch(analyzeImages({ setupId, imageIds: live, context }));
    }
  },
  {
    condition: ({ setupId, batchId }, { getState }) =>
      !getState().imageAnalyze.loadingMap[analyzeBatchKey(setupId, batchId)],
  }
);

const ImageAnalyzeThunk = {
  analyzeImages,
  analyzeBatch,
};

export default ImageAnalyzeThunk;
